import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom'; // Import useNavigate
import Layout from '@/components/layout/Layout'; // Use default import
import { useAuth } from '@/context/auth';
import { listSugarBabyProfiles, listSugarDaddyProfiles } from '@/services/sugar-profile.service';
import { SugarBabyProfile, SugarDaddyProfile, SugarBabyFilters, SugarDaddyFilters, LifestyleInterestTag, ArrangementTypeTag, SdSupportTag } from '@/types';
import { Skeleton } from '@/components/ui/skeleton'; // For loading state
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'; // For errors
import { Terminal, Filter, X as ClearIcon } from 'lucide-react';
import { useLanguageUtils } from '@/context/language-context'; // Import language utils
import { Button } from '@/components/ui/button'; // Import Button
import { Input } from '@/components/ui/input'; // Import Input for filter
import { Label } from '@/components/ui/label'; // Import Label
import { Checkbox } from '@/components/ui/checkbox'; // Import Checkbox
import { canCreateSugarBabyProfile, canCreateSugarDaddyProfile } from '@/context/auth/auth-utils'; // Import permission checks

// Simple placeholder components for profile lists
const SugarBabyList: React.FC<{ profiles: SugarBabyProfile[], getUrlWithLanguage: (path: string) => string }> = ({ profiles, getUrlWithLanguage }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {profiles.map(profile => (
      <Link 
        key={profile.id} 
        to={getUrlWithLanguage(`/sugar-relationships/sb/${profile.id}`)} // Link to detail page
        className="block border p-4 rounded-lg shadow hover:shadow-md transition-shadow"
      >
        {/* TODO: Add image */} 
        <Skeleton className="w-full h-32 rounded-md mb-2 bg-muted"/> 
        <h3 className="font-semibold">{profile.pseudo}</h3>
        <p className="text-sm text-muted-foreground">{profile.location?.city}, {profile.location?.country}</p>
      </Link>
    ))}
  </div>
);

const SugarDaddyList: React.FC<{ profiles: SugarDaddyProfile[], getUrlWithLanguage: (path: string) => string }> = ({ profiles, getUrlWithLanguage }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {profiles.map(profile => (
      <Link 
        key={profile.id} 
        to={getUrlWithLanguage(`/sugar-relationships/sd/${profile.id}`)} // Link to detail page
        className="block border p-4 rounded-lg shadow hover:shadow-md transition-shadow"
      >
        {/* TODO: Add image */} 
        <Skeleton className="w-full h-32 rounded-md mb-2 bg-muted"/>
        <h3 className="font-semibold">{profile.pseudo}</h3>
        <p className="text-sm text-muted-foreground">{profile.location?.city}, {profile.location?.country}</p>
        <p className="text-xs">Age: {profile.ageRange}</p>
        <p className="text-xs">Status: {profile.verificationStatus}</p>
      </Link>
    ))}
  </div>
);

// --- Constants for Filter Options (should match types/enums) ---
const lifestyleTags = ['studies', 'professional', 'hobbies', 'travel', 'gastronomy', 'artsCulture', 'luxury', 'sports', 'wellness'] as const;
const arrangementTypeTags = ['mentorship', 'financialSupport', 'experiences', 'networking', 'shortTerm', 'longTerm', 'platonic', 'discreet', 'public_limited', 'eventsCompanion', 'regularDates'] as const;
const sdSupportTags = ['mentorship', 'generousSupport', 'needsBasedSupport', 'gifts', 'travel', 'eventsAccess', 'networkingAccess', 'sharedExperiences'] as const;

const PAGE_SIZE = 12; // Define page size

const SugarRelationshipsPage: React.FC = () => {
  const { t } = useTranslation(['sugar', 'common', 'forms']); // Added 'forms' for labels
  const { user } = useAuth();
  const { getUrlWithLanguage } = useLanguageUtils(); // Get language util function
  const navigate = useNavigate(); // Hook for navigation
  const [profiles, setProfiles] = useState<SugarBabyProfile[] | SugarDaddyProfile[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  
  // --- Filter State --- 
  const [showFilters, setShowFilters] = useState(false);
  const [locationFilter, setLocationFilter] = useState('');
  // Tag filter states
  const [selectedLifestyle, setSelectedLifestyle] = useState<LifestyleInterestTag[]>([]);
  const [selectedArrangement, setSelectedArrangement] = useState<ArrangementTypeTag[]>([]);
  const [selectedSupport, setSelectedSupport] = useState<SdSupportTag[]>([]); // Only for SD view
  
  // Active filters derived from state
  const activeSbFilters: SugarBabyFilters = {
      ...(locationFilter && { locationCity: locationFilter }),
      ...(selectedLifestyle.length > 0 && { lifestyleInterests: selectedLifestyle }),
      ...(selectedArrangement.length > 0 && { arrangementTypes: selectedArrangement }),
  };
   const activeSdFilters: SugarDaddyFilters = {
       ...(locationFilter && { locationCity: locationFilter }),
       ...(selectedLifestyle.length > 0 && { lifestyleInterests: selectedLifestyle }),
       ...(selectedArrangement.length > 0 && { arrangementTypes: selectedArrangement }),
       ...(selectedSupport.length > 0 && { supportTypes: selectedSupport }),
       // verificationStatus: 'fully_verified', // Example: Always show verified only? Or add a filter UI
   };
  // --- End Filter State ---

  const userRole = user?.role;
  // Determine view type based on user role (simplified logic)
  // Potential SDs (clients, sugar_daddies) view SBs
  // Potential SBs (escorts, sugar_babies) view SDs
  const viewType: 'sb' | 'sd' | null =
      (userRole === 'client' || userRole === 'sugar_daddy') ? 'sb' :
      (userRole === 'escort' || userRole === 'sugar_baby') ? 'sd' :
      null; // Or handle other roles like admin if needed

  // Permissions checks
  const canCreateSB = canCreateSugarBabyProfile(user);
  const canCreateSD = canCreateSugarDaddyProfile(user);
  const hasSbProfile = !!user?.sugarBabyProfileId;
  const hasSdProfile = !!user?.sugarDaddyProfileId;

  // --- Fetching Logic --- 
  const fetchProfiles = useCallback(async () => {
    if (!viewType) {
      setError(t('sugar:error_invalid_role'));
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    // Keep existing profiles during loading for better UX?
    // setProfiles(null); 

    let result;
    const pagination = { page: currentPage, pageSize: PAGE_SIZE };
    try {
      if (viewType === 'sb') {
        result = await listSugarBabyProfiles(activeSbFilters, pagination);
      } else { // viewType === 'sd'
        result = await listSugarDaddyProfiles(activeSdFilters, pagination);
      }

      if (result.success && result.data) {
        setProfiles(result.data.profiles);
        setTotalCount(result.data.totalCount);
      } else {
        setError(result.error?.message || t('common:error_unknown'));
        setProfiles([]); // Clear profiles on error
        setTotalCount(0);
      }
    } catch (err: any) {
      setError(err.message || t('common:error_unexpected'));
      setProfiles([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [user, viewType, t, currentPage, activeSbFilters, activeSdFilters]); // Add dependencies

  useEffect(() => {
    if (user) {
        fetchProfiles();
    }
  }, [user, fetchProfiles]); // fetchProfiles is now stable due to useCallback
  // --- End Fetching Logic --- 

  // --- Event Handlers --- 
  const handleApplyFilters = () => {
      setCurrentPage(1); 
      // Fetch will be triggered by state changes included in fetchProfiles deps
      // Alternatively call fetchProfiles() directly if deps are complex/unstable
      fetchProfiles();
  };

  const clearFilters = () => {
      setLocationFilter('');
      setSelectedLifestyle([]);
      setSelectedArrangement([]);
      setSelectedSupport([]);
      setCurrentPage(1);
      // fetchProfiles(); // Call directly if needed
  };
  
  const handlePageChange = (newPage: number) => {
      if (newPage >= 1 && newPage <= Math.ceil(totalCount / PAGE_SIZE)) {
          setCurrentPage(newPage);
      }
  }
  
  // Handler for checkbox groups
  const handleCheckboxChange = <T extends string>(setState: React.Dispatch<React.SetStateAction<T[]>>, value: T) => {
      setState(prev => 
          prev.includes(value) 
              ? prev.filter(item => item !== value) 
              : [...prev, value]
      );
  };
  // --- End Event Handlers --- 

  const renderActionButtons = () => {
    // Show SB create/edit button
    if (canCreateSB) {
      return (
        <Button onClick={() => navigate(getUrlWithLanguage(hasSbProfile ? '/sugar-relationships/edit-sb' : '/sugar-relationships/create-sb'))}>
          {hasSbProfile ? t('sugar:button_edit_sb_profile') : t('sugar:button_create_sb_profile')}
        </Button>
      );
    }
    // Show SD create/edit button
    if (canCreateSD) {
        return (
          <Button onClick={() => navigate(getUrlWithLanguage(hasSdProfile ? '/sugar-relationships/edit-sd' : '/sugar-relationships/create-sd'))}>
            {hasSdProfile ? t('sugar:button_edit_sd_profile') : t('sugar:button_create_sd_profile')}
          </Button>
      );
    }
    return null;
  };

  // --- Rendering --- 
  const renderCheckboxFilterGroup = <T extends string>(
      labelKey: string, 
      options: readonly T[], 
      selectedState: T[], 
      setState: React.Dispatch<React.SetStateAction<T[]>>,
      tagNamespace: string // e.g., 'lifestyle', 'arrangement', 'sd_support'
  ) => (
      <div>
          <Label className="text-sm font-medium">{t(labelKey)}</Label>
          <div className="mt-2 space-y-2 max-h-40 overflow-y-auto pr-2"> {/* Added scroll */} 
              {options.map(option => (
                  <div key={option} className="flex items-center space-x-2">
                      <Checkbox 
                          id={`filter-${tagNamespace}-${option}`}
                          checked={selectedState.includes(option)}
                          onCheckedChange={() => handleCheckboxChange(setState, option)}
                      />
                      <Label htmlFor={`filter-${tagNamespace}-${option}`} className="text-sm font-normal cursor-pointer">
                          {t(`sugar:tags.${tagNamespace}.${option}`, option)}
                      </Label>
                  </div>
              ))}
          </div>
      </div>
  );

  const renderFilters = () => (
      <div className="bg-muted p-4 rounded-lg mb-6 space-y-4">
          <div className="flex justify-between items-center">
             <h3 className="text-lg font-semibold">{t('common:filters')}</h3>
             <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={clearFilters}><ClearIcon className="w-4 h-4 mr-1"/>{t('common:clear')}</Button>
                <Button size="sm" onClick={handleApplyFilters}>{t('common:apply')}</Button> { /* Apply button */}
             </div>
          </div>
          {/* Location Filter */}
          <div>
              <Label htmlFor="locationFilter" className="text-sm">{t('forms:label_location_city')}</Label>
              <Input 
                  id="locationFilter"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  placeholder={t('forms:placeholder_city_filter')}
                  className="mt-1"
              />
          </div>
          
           {/* Lifestyle Filter */}
           {renderCheckboxFilterGroup(
                'forms:label_lifestyleInterests', 
                lifestyleTags, 
                selectedLifestyle, 
                setSelectedLifestyle, 
                'lifestyle'
            )}
            
            {/* Arrangement Type Filter */}
            {renderCheckboxFilterGroup(
                'forms:label_arrangementType', 
                arrangementTypeTags, 
                selectedArrangement, 
                setSelectedArrangement, 
                'arrangement'
            )}
            
            {/* Support Type Filter (Only when viewing SDs) */}
            {viewType === 'sd' && renderCheckboxFilterGroup(
                'forms:label_supportType', 
                sdSupportTags, 
                selectedSupport, 
                setSelectedSupport, 
                'sd_support'
            )}
            
           {/* TODO: Add filters for age range (SD), verified status (SD) */} 
           
      </div>
  );

  const renderPagination = () => {
      const totalPages = Math.ceil(totalCount / PAGE_SIZE);
      if (totalPages <= 1) return null;

      return (
          <div className="mt-8 flex justify-center items-center space-x-2">
              <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)} 
                  disabled={currentPage <= 1}
              >
                  {t('common:previous')}
              </Button>
              <span className="text-sm">{t('common:page_indicator', { currentPage, totalPages })}</span>
              <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)} 
                  disabled={currentPage >= totalPages}
              >
                  {t('common:next')}
              </Button>
          </div>
      );
  }

  const renderContent = () => {
    if (!viewType && !isLoading) { // Handle invalid role explicitly
        return (
           <Alert variant="destructive">
             <Terminal className="h-4 w-4" />
             <AlertTitle>{t('common:error_title')}</AlertTitle>
             <AlertDescription>{error || t('sugar:error_invalid_role')}</AlertDescription>
           </Alert>
        );
    }
    // ... loading state ...
    if (isLoading && !profiles) { // Show skeleton only on initial load
         return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(PAGE_SIZE)].map((_, i) => <Skeleton key={i} className="h-48 rounded-lg" />)} 
            </div>
        );
    }
    // ... error state ...
     if (error) { /* ... */ }

    // --- No results state ---
     if (!isLoading && (!profiles || profiles.length === 0)) {
        return <p>{t(viewType === 'sb' ? 'sugar:no_sb_profiles_found' : 'sugar:no_sd_profiles_found')}</p>; // Specific message
     }

    // --- Display list --- 
    if (viewType === 'sb' && profiles) {
        return <SugarBabyList profiles={profiles as SugarBabyProfile[]} getUrlWithLanguage={getUrlWithLanguage} />;
    } else if (viewType === 'sd' && profiles) {
        return <SugarDaddyList profiles={profiles as SugarDaddyProfile[]} getUrlWithLanguage={getUrlWithLanguage} />;
    }
    
    return null; // Should not be reached if logic is correct
  };

  return (
    <Layout>
      <div className="container mx-auto py-8">
        <div className="flex justify-between items-center mb-4">
            <h1 className="text-3xl font-bold">
            {viewType === 'sb' ? t('sugar:sb_list_title') : viewType === 'sd' ? t('sugar:sd_list_title') : t('sugar:title')}
            </h1>
            <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
                    <Filter className="w-4 h-4 mr-1"/>
                    {t('common:filters')}
                </Button>
                {renderActionButtons()} 
            </div>
        </div>
        
        {/* Conditional Filter UI */} 
        {showFilters && renderFilters()}
        
        {renderContent()}
        {renderPagination()} 
      </div>
    </Layout>
  );
};

export default SugarRelationshipsPage; 